import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "../components/auth/ProtectedRoute";

// Eager: above-the-fold + critical
import Landing from "../pages/Landing";
import Login from "../pages/Login";
import Register from "../pages/Register";
import ForgotPassword from "../pages/ForgotPassword";
import ResetPassword from "../pages/ResetPassword";
import NotFound from "../pages/NotFound";

// Lazy: everything else — keeps initial bundle small
const Dashboard          = lazy(() => import("../pages/Dashboard"));
const AddAnimal          = lazy(() => import("../pages/AddAnimal"));
const AnimalDetail       = lazy(() => import("../pages/AnimalDetail"));
const EvaluationResult   = lazy(() => import("../pages/EvaluationResult"));
const Leaderboard        = lazy(() => import("../pages/Leaderboard"));
const Subscribe          = lazy(() => import("../pages/Subscribe"));
const PaymentSuccess     = lazy(() => import("../pages/PaymentSuccess"));
const AdminDashboard     = lazy(() => import("../pages/AdminDashboard"));
const Wallet             = lazy(() => import("../pages/Wallet"));
const PublicCertificate  = lazy(() => import("../pages/PublicCertificate"));
const Catalog            = lazy(() => import("../pages/Catalog"));
const TryFree            = lazy(() => import("../pages/TryFree"));
const OfficialEval       = lazy(() => import("../pages/OfficialEval"));
const ThqahProducts      = lazy(() => import("../pages/ThqahProducts"));
const Market             = lazy(() => import("../pages/Market"));
const Sell               = lazy(() => import("../pages/SellThroughThqah"));
const Pedigree           = lazy(() => import("../pages/Pedigree"));
const GrowHub           = lazy(() => import("../pages/GrowHub"));
const MateMatch         = lazy(() => import("../pages/MateMatch"));
const Onboarding         = lazy(() => import("../pages/Onboarding"));
const AdminQuickEdit     = lazy(() => import("../pages/AdminQuickEdit"));
// PharmacyApply intentionally removed — Pharmacy is Thqah-owned
const DocumentLivestock  = lazy(() => import("../pages/Documentation"));
const LivestockSpecs     = lazy(() => import("../pages/LivestockSpecs"));
const Pharmacy           = lazy(() => import("../pages/Pharmacy"));
const MyLivestock        = lazy(() => import("../pages/MyLivestock"));
const Compare            = lazy(() => import("../pages/Compare"));
const TamperCheck        = lazy(() => import("../pages/TamperCheck"));
const VetsList           = lazy(() => import("../pages/VetsList"));
const VetProfile         = lazy(() => import("../pages/VetProfile"));
const VetApply           = lazy(() => import("../pages/VetApply"));
const VetDashboard       = lazy(() => import("../pages/VetDashboard"));
const MyConsultations    = lazy(() => import("../pages/MyConsultations"));
const ConsultationDetail = lazy(() => import("../pages/ConsultationDetail"));
const PrivacyPage        = lazy(() => import("../pages/LegalPages").then((m) => ({ default: m.PrivacyPage })));
const TermsPage          = lazy(() => import("../pages/LegalPages").then((m) => ({ default: m.TermsPage })));
const RefundPolicy       = lazy(() => import("../pages/RefundPolicy"));
const ContactUs          = lazy(() => import("../pages/ContactUs"));
const About              = lazy(() => import("../pages/About"));
const HowItWorks         = lazy(() => import("../pages/HowItWorks"));
const CommunityGuidelines = lazy(() => import("../pages/CommunityPages").then((m) => ({ default: m.CommunityGuidelines })));
const EulaPage           = lazy(() => import("../pages/CommunityPages").then((m) => ({ default: m.EulaPage })));
const Settings           = lazy(() => import("../pages/Account"));
const Account            = lazy(() => import("../pages/Account"));
const MyApplications     = lazy(() => import("../pages/MyApplications"));
const AppStoreFrames     = lazy(() => import("../pages/marketing/AppStoreFrames"));

const Championships      = lazy(() => import("../pages/Championships"));
const ChampionshipDetail = lazy(() => import("../pages/ChampionshipDetail"));
const ChampionshipEnter  = lazy(() => import("../pages/ChampionshipEnter"));
const MyChampionships    = lazy(() => import("../pages/MyChampionships"));
const AiCompare          = lazy(() => import("../pages/ai/Compare"));
const AiMarketValue      = lazy(() => import("../pages/ai/MarketValue"));
const AiPhotoStudio      = lazy(() => import("../pages/ai/PhotoStudio"));
const PublicCompare      = lazy(() => import("../pages/ai/PublicCompare"));

// Marketplace (سوق ثقة + عرض للعامة)
const MarketplaceLanding  = lazy(() => import("../pages/marketplace/MarketplaceLanding"));
const MarketplaceCategory = lazy(() => import("../pages/marketplace/MarketplaceCategory"));
const MarketplaceVariants = lazy(() => import("../pages/marketplace/MarketplaceVariants"));
const MarketplaceListings = lazy(() => import("../pages/marketplace/MarketplaceListings"));
const MarketplaceCreate   = lazy(() => import("../pages/marketplace/MarketplaceCreate"));
const MarketplaceDetail   = lazy(() => import("../pages/marketplace/MarketplaceDetail"));
const AdminMarketplaceReview = lazy(() => import("../pages/marketplace/AdminMarketplaceReview"));
const EscrowDetail        = lazy(() => import("../pages/marketplace/EscrowDetail"));
const MarketplaceCheckout = lazy(() => import("../pages/marketplace/MarketplaceCheckout"));
const MyListings          = lazy(() => import("../pages/marketplace/MyListings"));

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading">
    <div className="h-10 w-10 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin" />
  </div>
);

export default function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public — eager */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Public — lazy */}
        <Route path="/try" element={<TryFree />} />
        <Route path="/official-eval" element={<OfficialEval />} />
        <Route path="/thqah-products" element={<ThqahProducts />} />
        <Route path="/championships" element={<Championships />} />
        <Route path="/championships/:slug" element={<ChampionshipDetail />} />
        <Route path="/championships/:slug/enter" element={<ProtectedRoute><ChampionshipEnter /></ProtectedRoute>} />
        <Route path="/my-championships" element={<ProtectedRoute><MyChampionships /></ProtectedRoute>} />
        <Route path="/ai/compare" element={<AiCompare />} />
        <Route path="/ai/market-value" element={<AiMarketValue />} />
        <Route path="/ai/photo-studio" element={<AiPhotoStudio />} />
        <Route path="/c/:id" element={<PublicCompare />} />
        <Route path="/vet-hub" element={<Navigate to="/thqah-products" replace />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/tamper" element={<TamperCheck />} />
        <Route path="/vets" element={<VetsList />} />
        <Route path="/vets/:id" element={<VetProfile />} />
        <Route path="/vet/apply"   element={<ProtectedRoute><VetApply /></ProtectedRoute>} />
        <Route path="/vet" element={<ProtectedRoute role="vet"><VetDashboard /></ProtectedRoute>} />
        <Route path="/consultations" element={<ProtectedRoute><MyConsultations /></ProtectedRoute>} />
        <Route path="/consultations/:id" element={<ProtectedRoute><ConsultationDetail /></ProtectedRoute>} />
        <Route path="/market" element={<Market />} />
        <Route path="/sell" element={<Sell />} />

        {/* Marketplace — اعرض حلالك */}
        <Route path="/marketplace" element={<MarketplaceLanding />} />
        <Route path="/marketplace/:tier" element={<MarketplaceCategory />} />
        {/* List directly under a category — variant becomes an in-page filter
            chip rather than a forced extra step. Keeps the flow short:
              public → category → listings → detail → buy */}
        <Route path="/marketplace/:tier/:category" element={<MarketplaceListings />} />
        {/* Optional deep-link with variant pre-filter (used by Thqah-market grid) */}
        <Route path="/marketplace/:tier/:category/variants" element={<MarketplaceVariants />} />
        <Route path="/marketplace/:tier/:category/new" element={<MarketplaceCreate />} />
        <Route path="/marketplace/:tier/:category/:variant" element={<MarketplaceListings />} />
        <Route path="/marketplace/listing/:listingId" element={<MarketplaceDetail />} />
        <Route path="/marketplace/checkout/:listingId" element={<ProtectedRoute><MarketplaceCheckout /></ProtectedRoute>} />
        <Route path="/marketplace/escrow/:eid" element={<ProtectedRoute><EscrowDetail /></ProtectedRoute>} />
        <Route path="/my-listings" element={<ProtectedRoute><MyListings /></ProtectedRoute>} />
        <Route path="/admin/marketplace" element={<ProtectedRoute role="admin"><AdminMarketplaceReview /></ProtectedRoute>} />
        <Route path="/races" element={<Navigate to="/" replace />} />
        <Route path="/race-eval" element={<Navigate to="/" replace />} />
        <Route path="/pedigree" element={<Pedigree />} />
        <Route path="/grow" element={<GrowHub />} />
        {/* Legacy: redirect /grow/product to Pharmacy (product was relocated there) */}
        <Route path="/grow/product" element={<Navigate to="/pharmacy" replace />} />
        <Route path="/grow/mate" element={<MateMatch />} />
        {/* Influencers section fully removed — joining is now via WhatsApp only */}
        <Route path="/influencers" element={<Navigate to="/" replace />} />
        <Route path="/influencer/apply" element={<Navigate to="/" replace />} />
        {/* Pharmacy is now a Thqah-owned section only — vendor apply route removed */}
        <Route path="/pharmacy/apply" element={<Navigate to="/pharmacy" replace />} />
        <Route path="/account/applications" element={<ProtectedRoute><MyApplications /></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/admin/quick-edit" element={<ProtectedRoute role="admin"><AdminQuickEdit /></ProtectedRoute>} />
        <Route path="/document" element={<ProtectedRoute><DocumentLivestock /></ProtectedRoute>} />
        <Route path="/specs" element={<LivestockSpecs />} />
        <Route path="/pharmacy" element={<Pharmacy />} />
        <Route path="/my-livestock" element={<ProtectedRoute><MyLivestock /></ProtectedRoute>} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/refund" element={<RefundPolicy />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/about" element={<About />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/guidelines" element={<CommunityGuidelines />} />
        <Route path="/eula" element={<EulaPage />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/account" element={<Account />} />
        <Route path="/marketing/appstore-frames" element={<AppStoreFrames />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/catalog" element={<Catalog />} />
        {/* Experts section fully removed — redirect all old links to home */}
        <Route path="/experts" element={<Navigate to="/" replace />} />
        <Route path="/experts/leaderboard" element={<Navigate to="/" replace />} />
        <Route path="/experts/:id" element={<Navigate to="/" replace />} />
        <Route path="/expert/apply" element={<Navigate to="/" replace />} />
        <Route path="/expert" element={<Navigate to="/" replace />} />
        <Route path="/subscribe" element={<Subscribe />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/share/cert/:id" element={<PublicCertificate />} />
        <Route path="/c/:id" element={<PublicCertificate />} />
        <Route path="/animals/:id" element={<AnimalDetail />} />
        <Route path="/evaluations/:id" element={<EvaluationResult />} />

        {/* Authenticated (any role) */}
        <Route path="/dashboard"      element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/wallet"         element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
        <Route path="/animals/new"    element={<ProtectedRoute><AddAnimal /></ProtectedRoute>} />

        {/* Role-gated */}
        <Route path="/admin"  element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
